/**
 * The sound design itself — fourteen cues, all synthesised.
 *
 * THE PALETTE, IN SOUND
 * The visual direction is a warm cream storybook with thick ink lines. The audio
 * equivalent is a **struck wooden bar**: marimba and kalimba. Every interaction
 * cue in this file is built from the same `pluck()` voice, so the whole game
 * sounds like one instrument being played rather than a bag of unrelated beeps.
 * Nothing here uses a sawtooth, a square, distortion, or a sustained buzz.
 *
 * CORRECT vs WRONG — READABLE WITHOUT PITCH
 * The design system's rule is "feedback teaches, never a bare Wrong". A buzzer
 * would contradict that in a quarter of a second. So the two cues are separated
 * on three independent axes, which means a player who cannot hear one of them
 * still gets the message from the others:
 *
 *   axis        correct                        wrong
 *   ─────────── ────────────────────────────── ──────────────────────────────
 *   contour     four DISCRETE steps, rising    one CONTINUOUS glide, falling
 *   timbre      bright, percussive attack      dark, soft breathy attack
 *   articulation staccato bar hits             legato with gentle vibrato
 *
 * `wrong` deliberately resolves onto a consonant interval and fades — it reads
 * as "aww, try again", never as an alarm. Nothing in this file is allowed to be
 * harsh, metallic or startling; a seven-year-old is going to hear it a hundred
 * times in three minutes.
 */
import { hz, noise, tone } from './context';

export type SfxId =
  | 'pickup'
  | 'inspect'
  | 'correct'
  | 'wrong'
  | 'step'
  | 'win'
  | 'lose'
  | 'tick'
  | 'uiClick'
  | 'uiOpen'
  | 'uiClose'
  | 'scan'
  | 'deposit'
  | 'bottleClink';

export interface SfxMeta {
  id: SfxId;
  group: string;
  label: string;
  /** One line a critic can check the sound against. */
  note: string;
}

/* ------------------------------------------------------------------ *
 * The house instrument
 * ------------------------------------------------------------------ */

/**
 * A struck wooden bar.
 *
 * Three partials plus a mallet click. The 4:1 partial is what makes a marimba
 * sound like a marimba (its bars are undercut to tune the first overtone to two
 * octaves up); the 2% detune on it keeps the stack from collapsing into a pure
 * harmonic series, which is the difference between "wooden" and "synthy". The
 * noise click is 14ms of bandpassed hiss — remove it and the note sounds like it
 * faded in rather than being hit.
 */
function pluck(f: number, at: number, dur: number, gain: number, rev = 0, bright = 1): void {
  tone({ freq: f, type: 'sine', at, dur, gain, attack: 0.003, reverb: rev, lp: 5400 });
  tone({ freq: f * 2, type: 'triangle', at, dur: dur * 0.45, gain: gain * 0.2 * bright, attack: 0.002 });
  tone({ freq: f * 4.02, type: 'sine', at, dur: dur * 0.28, gain: gain * 0.11 * bright, attack: 0.0015 });
  noise({
    at,
    dur: 0.014,
    gain: gain * 0.16 * bright,
    bp: Math.min(9000, f * 6),
    q: 1.6,
    attack: 0.001,
  });
}

/** A soft glassy bell, used only for shimmer on top of win/correct. */
function shimmer(f: number, at: number, dur: number, gain: number): void {
  tone({ freq: f, type: 'sine', at, dur, gain, attack: 0.04, reverb: 0.5, lp: 7000 });
  tone({ freq: f * 2.76, type: 'sine', at, dur: dur * 0.6, gain: gain * 0.3, attack: 0.05, reverb: 0.5 });
}

/* Note sets, frozen at module scope so no array is built per trigger. */
const CORRECT_ARP = [72, 76, 79, 84] as const; // C5 E5 G5 C6 — plain major, rising
const WIN_RUN = [72, 76, 79, 84, 88, 91] as const; // C5 E5 G5 C6 E6 G6
const WIN_CHORD = [76, 79, 84, 88] as const; // E5 G5 C6 E6 — open and bright
const WIN_SPARKLE = [96, 99, 103] as const;
const LOSE_FALL = [76, 74, 71, 67] as const; // E5 D5 B4 G4 — gentle, stepwise
const LOSE_REST = [67, 71] as const; // G4 + B4, a warm major third to land on

type SfxFn = (t: number, v: number, r: number) => void;

/* ------------------------------------------------------------------ *
 * The fourteen cues
 * ------------------------------------------------------------------ */

const DESIGNS: Record<SfxId, SfxFn> = {
  /** Item lifted. Two kalimba notes a fifth apart, flicked upward. */
  pickup(t, v, r) {
    pluck(hz(79) * r, t, 0.22 / r, 0.17 * v, 0.12);
    pluck(hz(86) * r, t + 0.062 / r, 0.26 / r, 0.15 * v, 0.16);
  },

  /**
   * Info panel opening on an object. A soft upward "lens" swell — no attack
   * transient at all, so it never competes with the pickup it usually follows.
   */
  inspect(t, v, r) {
    tone({
      freq: 520 * r,
      glideTo: 800 * r,
      type: 'triangle',
      at: t,
      dur: 0.24 / r,
      gain: 0.1 * v,
      attack: 0.014,
      lp: 2600,
      reverb: 0.14,
    });
    tone({
      freq: 260 * r,
      glideTo: 400 * r,
      type: 'sine',
      at: t,
      dur: 0.28 / r,
      gain: 0.06 * v,
      attack: 0.016,
    });
    noise({ at: t, dur: 0.2 / r, gain: 0.03 * v, bp: 900, bpTo: 2800, q: 3, attack: 0.03 });
  },

  /** Rising major arpeggio on the house marimba, with a bell hanging over it. */
  correct(t, v, r) {
    let at = t;
    for (const m of CORRECT_ARP) {
      pluck(hz(m) * r, at, 0.52 / r, 0.19 * v, 0.3);
      at += 0.066 / r;
    }
    shimmer(hz(96) * r, t + 0.2 / r, 1.0 / r, 0.045 * v);
  },

  /**
   * The "aww". A single voice sliding down a minor third with slow vibrato,
   * under a closing lowpass, plus a doubling octave below for body and a breath
   * of brown noise for warmth. Soft attack, no click, no dissonance.
   */
  wrong(t, v, r) {
    tone({
      freq: 392 * r,
      glideTo: 311.1 * r,
      type: 'triangle',
      at: t,
      dur: 0.46 / r,
      gain: 0.15 * v,
      attack: 0.05,
      lp: 950,
      lpTo: 520,
      vibratoHz: 5.5,
      vibratoCents: 14,
      reverb: 0.22,
    });
    tone({
      freq: 196 * r,
      glideTo: 155.6 * r,
      type: 'sine',
      at: t,
      dur: 0.5 / r,
      gain: 0.09 * v,
      attack: 0.06,
      lp: 700,
    });
    noise({ at: t, dur: 0.34 / r, gain: 0.022 * v, source: 'brown', lp: 700, attack: 0.08 });
  },

  /** Footfall. Deliberately near the threshold of notice. */
  step(t, v, r) {
    noise({ at: t, dur: 0.075 / r, gain: 0.05 * v, source: 'brown', lp: 340, attack: 0.004 });
    tone({ freq: 92 * r, glideTo: 70 * r, type: 'sine', at: t, dur: 0.06 / r, gain: 0.03 * v });
  },

  /** Run won: a six-note run, a held open chord, then three high sparkles. */
  win(t, v, r) {
    let at = t;
    for (const m of WIN_RUN) {
      pluck(hz(m) * r, at, 0.42 / r, 0.16 * v, 0.32);
      at += 0.088 / r;
    }
    const chordAt = t + 0.46 / r;
    for (const m of WIN_CHORD) {
      pluck(hz(m) * r, chordAt, 1.15 / r, 0.11 * v, 0.45, 0.7);
    }
    let sparkleAt = t + 0.62 / r;
    for (const m of WIN_SPARKLE) {
      shimmer(hz(m) * r, sparkleAt, 0.7 / r, 0.028 * v);
      sparkleAt += 0.12 / r;
    }
  },

  /**
   * Run lost. Falls, but lands on a major third and lingers — the audio version
   * of "have another go", not "you failed". Darker `bright` on the pluck rolls
   * the top partials off so it sits behind the result card's copy.
   */
  lose(t, v, r) {
    let at = t;
    for (const m of LOSE_FALL) {
      pluck(hz(m) * r, at, 0.5 / r, 0.14 * v, 0.28, 0.55);
      at += 0.135 / r;
    }
    const restAt = t + 0.58 / r;
    for (const m of LOSE_REST) {
      tone({
        freq: hz(m) * r,
        type: 'sine',
        at: restAt,
        dur: 1.1 / r,
        gain: 0.07 * v,
        attack: 0.09,
        lp: 1700,
        reverb: 0.42,
      });
    }
  },

  /** Countdown woodblock for the final seconds. Tiny on purpose. */
  tick(t, v, r) {
    noise({ at: t, dur: 0.014, gain: 0.045 * v, bp: 1800 * r, q: 8, attack: 0.001 });
    tone({ freq: 1400 * r, type: 'sine', at: t, dur: 0.035 / r, gain: 0.03 * v, attack: 0.001 });
  },

  /** Generic UI confirm. One dry bar hit — no reverb, so menus feel snappy. */
  uiClick(t, v, r) {
    pluck(hz(84) * r, t, 0.13 / r, 0.14 * v, 0);
  },

  /** Sheet / panel opening: two notes up. */
  uiOpen(t, v, r) {
    pluck(hz(79) * r, t, 0.19 / r, 0.13 * v, 0.1);
    pluck(hz(86) * r, t + 0.055 / r, 0.24 / r, 0.12 * v, 0.14);
  },

  /** Sheet / panel closing: the same two notes, mirrored and dulled. */
  uiClose(t, v, r) {
    pluck(hz(86) * r, t, 0.16 / r, 0.11 * v, 0.08, 0.6);
    pluck(hz(79) * r, t + 0.055 / r, 0.2 / r, 0.11 * v, 0.1, 0.6);
  },

  /**
   * Game 2's identification machine reading a bottle. A friendly rising sweep
   * with a bandpass chasing it, resolved by a confirm blip so the player knows
   * the readout is finished rather than still counting.
   */
  scan(t, v, r) {
    tone({
      freq: 300 * r,
      glideTo: 1150 * r,
      type: 'triangle',
      at: t,
      dur: 0.46 / r,
      gain: 0.075 * v,
      attack: 0.03,
      lp: 3200,
      reverb: 0.18,
    });
    noise({ at: t, dur: 0.5 / r, gain: 0.04 * v, bp: 420, bpTo: 3000, q: 6, attack: 0.05 });
    pluck(hz(88) * r, t + 0.5 / r, 0.24 / r, 0.13 * v, 0.2);
  },

  /** Bottle dropped into a bin: a soft body thunk, then a two-note confirm. */
  deposit(t, v, r) {
    noise({ at: t, dur: 0.1 / r, gain: 0.12 * v, source: 'brown', lp: 280, attack: 0.003 });
    tone({ freq: 118 * r, glideTo: 78 * r, type: 'sine', at: t, dur: 0.17 / r, gain: 0.1 * v });
    pluck(hz(72) * r, t + 0.12 / r, 0.24 / r, 0.12 * v, 0.18);
    pluck(hz(79) * r, t + 0.19 / r, 0.3 / r, 0.11 * v, 0.22);
  },

  /**
   * Plastic bottles knocking together. Two INHARMONIC partials (ratio 1.517, not
   * a musical interval) is what makes a tap read as an object rather than a
   * note, and a fresh random detune each call stops a pile of bottles from
   * sounding like one bottle cloned.
   */
  bottleClink(t, v, r) {
    const wobble = 1 + (Math.random() - 0.5) * 0.05;
    const f = 1180 * r * wobble;
    tone({ freq: f, type: 'triangle', at: t, dur: 0.13 / r, gain: 0.1 * v, attack: 0.0015, lp: 6000 });
    tone({ freq: f * 1.517, type: 'sine', at: t, dur: 0.1 / r, gain: 0.06 * v, attack: 0.0015 });
    noise({ at: t, dur: 0.01, gain: 0.05 * v, hp: 3000, attack: 0.0008 });
  },
};

/** Render one cue at an absolute context time. Never throws on unknown input. */
export function renderSfx(id: SfxId, at: number, volume: number, rate: number): void {
  DESIGNS[id](at, volume, rate);
}

/**
 * Minimum seconds between retriggers of the same cue.
 *
 * Only the cues a game loop can fire many times a second are limited. Everything
 * else is 0, because throttling a deliberate player action makes the game feel
 * broken — worse than the doubled sound it prevents.
 */
export const SFX_THROTTLE: Record<SfxId, number> = {
  pickup: 0.05,
  inspect: 0.05,
  correct: 0,
  wrong: 0,
  step: 0.09,
  win: 0.4,
  lose: 0.4,
  tick: 0.12,
  uiClick: 0.04,
  uiOpen: 0.04,
  uiClose: 0.04,
  scan: 0.1,
  deposit: 0.06,
  bottleClink: 0.035,
};

/** Ordered catalogue, used by the audit panel and by nothing else. */
export const SFX_META: readonly SfxMeta[] = [
  {
    id: 'correct',
    group: 'Feedback',
    label: 'correct',
    note: 'Rising 4-note major arpeggio + bell. Discrete, bright, percussive.',
  },
  {
    id: 'wrong',
    group: 'Feedback',
    label: 'wrong',
    note: 'Falling glide with vibrato under a closing filter. Soft, dark, encouraging.',
  },
  { id: 'win', group: 'Feedback', label: 'win', note: '6-note run, held open chord, three sparkles.' },
  {
    id: 'lose',
    group: 'Feedback',
    label: 'lose',
    note: 'Stepwise fall resolving onto a warm major third. Never a buzzer.',
  },
  { id: 'pickup', group: 'Interaction', label: 'pickup', note: 'Two kalimba notes a fifth apart, flicked up.' },
  { id: 'inspect', group: 'Interaction', label: 'inspect', note: 'Soft upward lens swell, no attack transient.' },
  { id: 'step', group: 'Interaction', label: 'step', note: 'Brown-noise footfall, just above the noise floor.' },
  { id: 'uiClick', group: 'Interface', label: 'uiClick', note: 'One dry bar hit. No reverb — menus feel snappy.' },
  { id: 'uiOpen', group: 'Interface', label: 'uiOpen', note: 'Two notes up, sheet entering.' },
  { id: 'uiClose', group: 'Interface', label: 'uiClose', note: 'The same two notes mirrored and dulled.' },
  { id: 'tick', group: 'Interface', label: 'tick', note: 'Countdown woodblock for the last seconds.' },
  { id: 'scan', group: 'Factory', label: 'scan', note: 'Rising sweep with chasing bandpass, then a confirm blip.' },
  { id: 'deposit', group: 'Factory', label: 'deposit', note: 'Soft body thunk into the bin, then a two-note confirm.' },
  {
    id: 'bottleClink',
    group: 'Factory',
    label: 'bottleClink',
    note: 'Inharmonic tap, randomly detuned so a pile never machine-guns.',
  },
];

/** Every id, in catalogue order. */
export const SFX_IDS: readonly SfxId[] = SFX_META.map((m) => m.id);
